import { ReactNode, useState } from 'react'

/**
 * SPEC-018 A4 — shared cinematic primitives for the auth threshold screens
 * (ADR-084). Marks, buttons, and the near-black stage that every threshold
 * sits in. Ported faithfully from the ratified design; interactive elements
 * carry the `at-focusable` class so the scoped `:focus-visible` ring applies.
 */

interface MicrosoftMarkProps {
  size?: number
  color?: string
}

/**
 * The Microsoft four-square mark, monochrome so it can sit on the gold button.
 *
 * @param size - Square footprint in pixels. Defaults to 18.
 * @param color - Fill color for the four squares. Defaults to currentColor.
 * @returns The decorative Microsoft mark (aria-hidden).
 */
export function MicrosoftMark({ size = 18, color = 'currentColor' }: MicrosoftMarkProps) {
  const gap = Math.round(size * 0.12)
  const square = (size - gap) / 2
  const cell = { width: square, height: square, background: color, borderRadius: 1 }
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-grid',
        gridTemplateColumns: `${square}px ${square}px`,
        gap,
        width: size,
        height: size,
      }}
    >
      <span style={cell} />
      <span style={cell} />
      <span style={cell} />
      <span style={cell} />
    </span>
  )
}

interface WarningMarkProps {
  size?: number
}

/**
 * A restrained gold warning triangle — never a red shout — for the auth-error
 * threshold.
 *
 * @param size - Icon size in pixels. Defaults to 60.
 * @returns The warning mark SVG.
 */
export function WarningMark({ size = 60 }: WarningMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      role="img"
      aria-label="Warning"
      style={{ display: 'block' }}
    >
      <path
        d="M24 6 L44 40 H4 Z"
        stroke="var(--gold)"
        strokeWidth="1.25"
        strokeLinejoin="round"
        fill="color-mix(in srgb, var(--color-primary) 6%, transparent)"
      />
      <line x1="24" y1="19" x2="24" y2="30" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="24" cy="34.5" r="1.15" fill="var(--gold)" />
    </svg>
  )
}

/**
 * The small wordmark at the top of every threshold.
 *
 * @returns The TC Music Hub wordmark.
 */
export function WordMark() {
  return (
    <div className="fade" style={{ display: 'flex', alignItems: 'center', gap: 10, animationDelay: '200ms' }}>
      <span style={{ width: 7, height: 7, background: 'var(--gold)', borderRadius: '50%', display: 'block' }} />
      <span
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: '.7rem',
          fontWeight: 600,
          letterSpacing: '.26em',
          textTransform: 'uppercase',
          color: 'var(--faint)',
        }}
      >
        TC Music Hub
      </span>
    </div>
  )
}

interface PrimaryButtonProps {
  children: ReactNode
  onClick?: () => void
  autoFocus?: boolean
  icon?: ReactNode
  testId?: string
}

/**
 * The gold primary action. Meets the ≥44px target size, shows a visible focus
 * ring via `at-focusable`, and lifts on hover with the cinematic easing.
 *
 * @param children - Button label.
 * @param onClick - Click handler.
 * @param autoFocus - Whether the button receives focus on mount (keyboard-first).
 * @param icon - Optional leading icon (e.g. the Microsoft mark).
 * @param testId - Optional data-testid (e.g. 'login-button').
 * @returns The primary button.
 */
export function PrimaryButton({ children, onClick, autoFocus, icon, testId }: PrimaryButtonProps) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      className="at-focusable"
      data-testid={testId}
      onClick={onClick}
      autoFocus={autoFocus}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        minHeight: 52,
        padding: '0 28px',
        fontFamily: 'var(--font-ui)',
        fontSize: '.98rem',
        fontWeight: 600,
        letterSpacing: '.01em',
        whiteSpace: 'nowrap',
        color: 'var(--ink)',
        border: 'none',
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'transform .25s var(--ease-cinematic), box-shadow .25s var(--ease-cinematic), background .2s ease',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover
          ? '0 14px 40px -12px color-mix(in srgb, var(--color-primary) 55%, transparent)'
          : '0 8px 28px -16px color-mix(in srgb, var(--color-primary) 45%, transparent)',
        background: hover ? 'color-mix(in srgb, var(--color-primary) 92%, white)' : 'var(--gold)',
      }}
    >
      {icon}
      {children}
    </button>
  )
}

interface GhostButtonProps {
  children: ReactNode
  onClick?: () => void
  testId?: string
}

/**
 * The quiet secondary action (return home / sign out). Same ≥44px target and
 * focus ring; a hairline border that warms to gold on hover.
 *
 * @param children - Button label.
 * @param onClick - Click handler.
 * @param testId - Optional data-testid.
 * @returns The ghost button.
 */
export function GhostButton({ children, onClick, testId }: GhostButtonProps) {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      className="at-focusable"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 52,
        padding: '0 24px',
        fontFamily: 'var(--font-ui)',
        fontSize: '.95rem',
        fontWeight: 500,
        letterSpacing: '.01em',
        color: hover ? 'var(--paper)' : 'var(--muted)',
        background: 'transparent',
        border: '1px solid ' + (hover ? 'var(--hair-gold)' : 'var(--hair)'),
        borderRadius: 4,
        cursor: 'pointer',
        transition: 'color .2s ease, border-color .2s ease',
      }}
    >
      {children}
    </button>
  )
}

interface ThresholdCanvasProps {
  children: ReactNode
  glow?: boolean
  testId?: string
}

/**
 * The shared near-black stage every threshold sits in: wordmark rail, centered
 * stage, provider footer, plus the grain + vignette atmosphere layers. The
 * root carries the `auth-threshold` scope so the design tokens resolve.
 *
 * @param children - The screen's centered content.
 * @param glow - Whether to render the gold radial glow. Defaults to true.
 * @param testId - Optional data-testid applied to the stage root.
 * @returns The full-bleed threshold canvas.
 */
export function ThresholdCanvas({ children, glow = true, testId }: ThresholdCanvasProps) {
  return (
    <div
      className="auth-threshold"
      data-testid={testId}
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        background: 'var(--ink)',
        overflow: 'hidden',
      }}
    >
      {glow && (
        <div
          className="glow-in"
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '30%',
            left: '50%',
            width: 'min(1100px, 120vw)',
            height: 'min(1100px, 120vw)',
            transform: 'translate(-50%, -50%)',
            background: 'radial-gradient(circle, var(--gold-glow) 0%, transparent 62%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      )}
      <div className="grain" aria-hidden="true" />
      <div className="vignette" aria-hidden="true" />

      <header style={{ position: 'relative', zIndex: 3, padding: 'clamp(24px, 4vw, 48px)' }}>
        <WordMark />
      </header>

      <main
        style={{
          position: 'relative',
          zIndex: 3,
          display: 'flex',
          alignItems: 'center',
          padding: '0 clamp(24px, 8vw, 120px)',
          minHeight: 0,
        }}
      >
        {children}
      </main>

      <footer
        className="fade"
        style={{
          position: 'relative',
          zIndex: 3,
          animationDelay: '900ms',
          padding: 'clamp(20px, 4vw, 40px) clamp(24px, 8vw, 120px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          borderTop: '1px solid var(--hair)',
          fontFamily: 'var(--font-ui)',
          fontSize: '.72rem',
          letterSpacing: '.04em',
          color: 'var(--faint)',
        }}
      >
        <span>Secured by Microsoft Entra ID</span>
        <span>TC Music Hub</span>
      </footer>
    </div>
  )
}
